import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import MainFooter from '@/components/MainFooter';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Download, ExternalLink, FileText, ArrowLeft, User } from 'lucide-react';
import { getReportById, Report, getCategoryLabel } from '@/data/reports';
import { ImageOptimizer } from '@/components/common/ImageOptimizer';

// Helper functions for meta tag management
const updateMetaTag = (attribute: string, name: string, content: string) => {
  if (!content) return;
  
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

const updateCanonicalLink = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', url);
  } else {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', url);
    document.head.appendChild(canonical);
  }
};

// Helper function to get category colors
const getCategoryColor = (category: Report['category']) => {
  switch (category) {
    case 'survey':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'research':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'ebooks':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const SingleReport = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);

  useEffect(() => {
    if (reportId) {
      const foundReport = getReportById(reportId);
      if (foundReport) {
        setReport(foundReport);
        
        // Set meta tags directly
        const siteOrigin = window.location.origin;
        const pageUrl = window.location.href;
        const description = foundReport.summary || foundReport.longDescription || 'Read the latest research and reports from Cutshort.';
        const ogImage = foundReport.image?.startsWith('http') ? foundReport.image : `${siteOrigin}${foundReport.image}`;
        const keywords = foundReport.keywords?.join(', ') || '';
        
        // Update document title
        document.title = `${foundReport.title} - Cutshort`;
        
        // Update meta tags
        updateMetaTag('name', 'description', description);
        updateMetaTag('name', 'keywords', keywords);
        updateMetaTag('property', 'og:title', `${foundReport.title} - Cutshort`);
        updateMetaTag('property', 'og:description', description);
        updateMetaTag('property', 'og:type', 'article');
        updateMetaTag('property', 'og:url', pageUrl);
        updateMetaTag('property', 'og:image', ogImage);
        updateMetaTag('name', 'twitter:title', `${foundReport.title} - Cutshort`);
        updateMetaTag('name', 'twitter:description', description);
        updateMetaTag('name', 'twitter:image', ogImage);
        updateMetaTag('property', 'twitter:card', 'summary_large_image');
        
        // Update canonical link
        updateCanonicalLink(pageUrl);
        
        console.log('Report loaded:', foundReport.title);
      } else {
        console.log('Report not found:', reportId);
        // Report not found, redirect to reports page
        navigate('/reports');
      }
    }
  }, [reportId, navigate]);

  if (!report) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-600 mb-2">Loading...</h2>
            <p className="text-gray-500">Please wait while we load the report.</p>
          </div>
        </div>
        <MainFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      
      {/* Back Button */}
      <div className="pt-24 pb-4">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/reports')}
            className="mb-4 text-brand-darkgrey hover:text-brand-blue"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
        </div>
      </div>

      {/* Report Header */}
      <section className="pb-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Category Badge */}
            <Badge 
              variant="outline" 
              className={`mb-4 ${getCategoryColor(report.category)} border`}
            >
              {getCategoryLabel(report.category)}
            </Badge>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-brand-darkgrey mb-6">
              {report.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-brand-grey">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{new Date(report.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              {report.tags && report.tags.length > 0 && (
                <div className="flex items-center gap-2">
                  <span>Tags:</span>
                  <div className="flex flex-wrap gap-1">
                    {report.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <p className="text-lg text-brand-grey mb-8 leading-relaxed">
              {report.longDescription}
            </p>

            {/* Download Button */}
            {report.downloadUrl && (
              <div className="mb-8">
                <Button
                  className="bg-brand-blue hover:bg-brand-blue/90 text-white"
                  onClick={() => window.open(report.downloadUrl, '_blank')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* Report Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* PDF Viewer - Left Side */}
              <div className="lg:col-span-2">
                {report.pdfUrl ? (
                  <div className="w-full h-[800px] border border-gray-200 rounded-lg overflow-hidden">
                    <iframe
                      src={`https://drive.google.com/file/d/${report.pdfUrl.split('/file/d/')[1]?.split('/view')[0]}/preview`}
                      width="100%"
                      height="100%"
                      className="border-0"
                      title={report.title}
                    />
                  </div>
                ) : (
                  <div className="w-full h-[800px] border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">PDF not available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Report Details - Right Side */}
              <div className="lg:col-span-1">
                {/* Report Description */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-brand-darkgrey mb-4">
                      About This Report
                    </h3>
                    <p className="text-brand-grey leading-relaxed mb-4">
                      {report.longDescription}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {report.tags?.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Report Sections */}
                {report.sections && report.sections.length > 0 && (
                  <Card className="mb-6">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-brand-darkgrey mb-4">
                        Report Contents
                      </h3>
                      <div className="space-y-4">
                        {report.sections.map((section) => (
                          <div key={section.id}>
                            <h4 className="font-semibold text-brand-darkgrey text-sm mb-2">
                              {section.heading}
                            </h4>
                            <div className="text-xs text-brand-grey leading-relaxed">
                              {section.content.split('\n').map((paragraph, index) => (
                                <p key={index} className="mb-2">
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Authors */}
                {report.authors && report.authors.length > 0 && (
                  <Card className="mb-6">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-brand-darkgrey mb-4">
                        Authors
                      </h3>
                      <div className="space-y-4">
                        {report.authors.map((author) => (
                          <div key={author.id} className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                              {author.image ? (
                                <ImageOptimizer
                                  src={author.image}
                                  alt={author.name}
                                  className="w-full h-full object-cover"
                                  width={48}
                                  height={48}
                                  loading="lazy"
                                  objectFit="cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                  <User className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-brand-darkgrey text-sm">
                                {author.name}
                              </h4>
                              <p className="text-xs text-brand-grey mb-1">
                                {author.title}
                              </p>
                              <p className="text-xs text-brand-grey">
                                {author.bio}
                              </p>
                              {author.linkedinUrl && (
                                <a
                                  href={author.linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-brand-blue hover:underline mt-1"
                                >
                                  LinkedIn
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Download Card */}
                {report.downloadUrl && (
                  <Card>
                    <CardContent className="p-6 text-center">
                      <FileText className="h-12 w-12 text-brand-blue mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-brand-darkgrey mb-2">
                        Download Report
                      </h3>
                      <p className="text-sm text-brand-grey mb-4">
                        Get the full report as a PDF
                      </p>
                      <Button
                        className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white"
                        onClick={() => window.open(report.downloadUrl, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-blue to-purple-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Stay Updated with Our Latest Research
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get notified when we publish new reports and insights.
          </p>
          <a 
            href="https://newsletter.cutshort.io/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-brand-blue px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Subscribe to Newsletter
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>

      <MainFooter />
    </div>
  );
};

export default SingleReport;
